import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

import { differenceInCalendarDays } from 'date-fns';

import { ChatClient, ChatDto } from '@app/api';

import { totalDifferenceInMinutes } from '@util/cer-date';
import { CerLocaleService } from '@ui/cer-ui-locale.service';
import { CerSessionService } from 'src/cer/cer-session/cer-session.service';

@Injectable({
  providedIn: 'root'
})
export class CerAppChatService implements OnDestroy {

  private subscriptionManager$: Subscription = new Subscription();

  public chats$ = new Subject<ChatDto[]>();

  constructor(
    private chatClient: ChatClient,
    private localeService: CerLocaleService,
    private session: CerSessionService) {
  }

  public ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
  }

  private userId(): number {
    return this.session.user.id();
  }

  public chatsSet(chats: ChatDto[]) {
    var userId: number = this.userId();
    var chats = chats.filter(chat => chat.isPrivate == false || chat.createdBy == userId);
    this.chats$.next(chats);
  }

  public chatPost(txt: string, refTableId: number, refRowId: number, chats: ChatDto[], isPrivate: boolean): Promise<ChatDto[]> {
    return new Promise<ChatDto[]>((resolve, reject) => {
      let chat: ChatDto = new ChatDto();
      chat.id = null;
      chat.createdBy = this.userId();
      chat.modifiedBy = chat.createdBy;
      chat.created = new Date();
      chat.modified = chat.created;
      chat.txt = txt;
      chat.refTableId = refTableId;
      chat.refRowId = refRowId;
      chat.isPrivate = isPrivate;
      this.chatToChatsSync(chats, chat);

      this.chatClient.post(chat).subscribe(
        data => {
          var isChanged: boolean = false;
          data.forEach(chat => {
            this.prepareChatDates(chat);
            if (this.chatToChatsSync(chats, chat)) {
              isChanged = true;
            }
          });
          if (isChanged) {
            this.chatsSet(chats);
            resolve(chats);
          }
        },
        error => {
          alert("Kan ikke opdatere chat");
          reject(chats);
        });
    });
  }

  public chatMarkRead(refTableId: number, refRowId: number) {
    this.chatClient.markReadThread(refTableId, refRowId).subscribe({
      next:
        (data) => {
        },
      error: (error) => {
        alert("Kan ikke læse chat");
      }
    });
  }

  //public refreshNotifications(chat) {
  //this.chatService.chatPosted.subscribe(
  //   this.notificationService.userNotificationsAddChats(this.userNotifications.getValue(), data))
  //}

  private prepareChatDates(chat: ChatDto) {
    chat.created = new Date(chat.created);
    chat.modified = new Date(chat.modified);
    if (chat.chatUsers) {
      chat.chatUsers.forEach(cu => {
        cu.created = new Date(cu.created);
        cu.modified = new Date(cu.modified);
      });
    }
  }

  public isChatsChanged(chats: ChatDto[], newChats: ChatDto[], refTableId: number, refRowId: number): boolean {
    if ((chats == null || chats.length == 0) && (newChats.length > 0 && newChats[0].refTableId == refTableId && newChats[0].refRowId == refRowId)) {
      chats = newChats;
      return true;
    }
    var isChanged: boolean = false;
    chats.forEach(chat =>
      isChanged = isChanged || this.isChatChanged(chats, chat, refTableId, refRowId)
    );
    return isChanged;
  }

  private isChatChanged(chats: ChatDto[], chat: ChatDto = null, refTableId: number, refRowId: number): boolean {
    var chatsChanged: boolean = false;
    if (chat.refRowId == refRowId && chat.refTableId == refTableId) {
      chatsChanged = this.chatToChatsSync(chats, chat);
    }
    return chatsChanged;
  }

  public chatToChatsSync(chats: ChatDto[], chatPosted: ChatDto = null): boolean {
    var chat = Object.assign({}, chatPosted);
    var isChanged: boolean = false;
    if (chats == null) {
      chats = [chat];
      return true;
    }
    var n = chats.length;
    if (n > 0) {
      for (var i = n - 1; i >= 0; i--) {
        var check = chats[i];
        if (check.id == null) {
          chats.splice(i, 1);
          isChanged = true;
        }
        else if (check.id == chat.id) {
          if (JSON.stringify(chat) === JSON.stringify(check)) {
            return false;
          }
          else {
            chats.splice(i, 1);
            isChanged = true;
          };
        }
      }
    }
    if (chat != null) {
      chats.push(chat);
      isChanged = true;
    }
    return isChanged;
  }


  // UI Helpers
  public chatUIAvatar(chat: ChatDto): string {
    return this.session.users.avatarById(chat.createdBy);
  }

  public chatUIIsOwn(chat: ChatDto): boolean {
    return chat && chat.createdBy == this.userId();
  }

  public userNameByChatCreatedBy(chat: ChatDto): string {
    return this.session.users.userNameById(chat.createdBy);
  }


  // Relative UI helpers

  public chatUIShowCreatedBy(idx: number, chats: ChatDto[]): boolean {
    return ((idx == 0 || chats[idx].createdBy != chats[idx - 1].createdBy) && !this.chatUIIsOwn(chats[idx]));
  }

  public chatUIShowDate(data: ChatDto, idx: number, chats: ChatDto[]): string {
    var dateStr: string = null;

    var created: Date = new Date(data.created);
    var now: Date = new Date();
    var previous: Date = (idx > 0) ? new Date(chats[idx - 1].created) : now;
    var diffDaysNow: number = differenceInCalendarDays(created, now);
    var diffDaysPrev: number = differenceInCalendarDays(created, previous);
    var diffMinutesPrev: number = Math.abs(totalDifferenceInMinutes(created, previous));

    if ((idx == 0 && diffMinutesPrev > 5)  /* When first posted a little while ago */ ||
      (diffDaysNow == 0 && diffMinutesPrev > 5) ||
      (diffDaysNow != 0 && diffDaysPrev != 0)) {
      dateStr = this.localeService.formatDateRelative(created, previous);
    }
    return dateStr;
  }

  public chatUIListItemClass(data: ChatDto, idx: number, chats: ChatDto[]): string {
    var isOwn: boolean = this.chatUIIsOwn(data);
    var sameUserBefore: boolean = (idx > 0 && data.createdBy == chats[idx - 1].createdBy);
    var sameUserAfter: boolean = (idx < chats.length - 1 && data.createdBy == chats[idx + 1].createdBy);
    var baseclass: string = isOwn ? "own" : "other";
    var classes: string = "chat-list-item " + baseclass;
    if (sameUserBefore) {
      classes = classes + " " + baseclass + "-before";
    }
    if (sameUserAfter) {
      classes = classes + " " + baseclass + "-after";
    }
    if (data.isPrivate) {
      classes = classes + " chat-list-item-private";
    }
    return classes;
  }

}
